<template>
  <!-- No backdrop-click close: a stray click outside must not discard a
       part-typed email/password. Close with the X button or Escape. -->
  <div class="auth-modal-backdrop">
    <div class="auth-modal-container">
      <div class="auth-modal-header">
        <h2 class="auth-modal-title">
          <i class="fas fa-sign-in-alt"></i>
          {{ $t('auth.login.title') || 'Sign In' }}
        </h2>
        <button class="auth-modal-close" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <form @submit.prevent="handleLogin" class="auth-form">
        <div v-if="activatedBanner" class="success-banner">
          <i class="fas fa-check-circle"></i>
          Account activated — please log in to continue.
        </div>

        <div v-if="invitationContext" class="invitation-banner">
          <i class="fas fa-envelope-open-text"></i>
          <span>
            <template v-if="invitationContext.org_name">
              You've been invited to join
              <strong>{{ invitationContext.org_name }}</strong>.
            </template>
            <template v-else>
              You've been invited to join an organization.
            </template>
            Sign in to accept — we'll surface the invitation right after.
          </span>
        </div>

        <div v-if="errorMessage" class="error-message">
          <i class="fas fa-exclamation-circle"></i>
          {{ errorMessage }}
        </div>

        <div class="form-group">
          <label for="email">
            <i class="fas fa-envelope"></i>
            {{ $t('auth.login.email') || 'Email' }}
          </label>
          <input
            id="email"
            v-model="form.email"
            type="email"
            required
            :disabled="loading"
            :placeholder="$t('auth.login.emailPlaceholder') || 'Enter your email'"
            autocomplete="username"
            class="form-input"
          >
        </div>

        <div class="form-group">
          <label for="password">
            <i class="fas fa-lock"></i>
            {{ $t('auth.login.password') || 'Password' }}
          </label>
          <input
            id="password"
            v-model="form.password"
            type="password"
            required
            :disabled="loading"
            :placeholder="$t('auth.login.passwordPlaceholder') || 'Enter your password'"
            autocomplete="current-password"
            class="form-input"
          >
        </div>

        <div class="form-options">
          <label class="remember-me">
            <input
              v-model="form.rememberMe"
              type="checkbox"
              :disabled="loading"
            >
            <span>{{ $t('auth.login.rememberMe') || 'Remember me' }}</span>
          </label>
          <a href="#" @click.prevent="$emit('show-forgot-password')" class="forgot-password-link">
            {{ $t('auth.login.forgotPassword') || 'Forgot password?' }}
          </a>
        </div>

        <button
          type="submit"
          :disabled="loading || !canAttemptLogin"
          class="btn btn-primary btn-block"
        >
          <i v-if="loading" class="fas fa-spinner fa-spin"></i>
          <span v-else>
            <i class="fas fa-sign-in-alt"></i>
            {{ $t('auth.login.submit') || 'Sign In' }}
          </span>
        </button>

        <div class="auth-footer">
          <p>
            {{ $t('auth.login.noAccount') || "Don't have an account?" }}
            <a href="#" @click.prevent="$emit('show-register')" class="auth-link">
              {{ $t('auth.login.signUp') || 'Sign up' }}
            </a>
          </p>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { authService } from '@/services/auth-api'

export default {
  name: 'LoginModal',
  emits: ['close', 'show-register', 'show-forgot-password'],
  data() {
    return {
      form: {
        email: '',
        password: '',
        rememberMe: false
      },
      loading: false,
      errorMessage: '',
      // Surfaced via ?activated=1 query param after Activate.vue redirects
      activatedBanner: false
    }
  },
  computed: {
    ...mapGetters('auth', ['canAttemptLogin']),
    ...mapState(['invitationContext'])
  },
  mounted() {
    if (this.$route?.query?.activated === '1') {
      this.activatedBanner = true
    }
    // When the user lands here from an invitation email link and the
    // invitee already has an account, App.vue stashes the resolved
    // context in vuex. Pre-fill the email so they only need to type
    // their password.
    if (this.invitationContext?.email) {
      this.form.email = this.invitationContext.email
    }
  },
  methods: {
    ...mapActions('auth', ['login']),
    ...mapActions(['showNotification']),
    
    async handleLogin() {
      this.errorMessage = ''
      
      if (!this.canAttemptLogin) {
        this.errorMessage = 'Too many login attempts. Please try again later.'
        return
      }
      
      this.loading = true
      
      try {
        await this.login({
          email: this.form.email,
          password: this.form.password,
          rememberMe: this.form.rememberMe
        })

        // Explicit Credential Management API hint. SPA logins use AJAX
        // submission with no full-page navigation, so Chrome's "save
        // password?" heuristic sometimes skips the prompt. This call
        // tells the browser the just-submitted creds were a successful
        // login and asks it to consider saving them. Browser still
        // honours user preferences ("never save for this site"); this
        // does not bypass them.
        if (typeof window.PasswordCredential === 'function') {
          try {
            const cred = new window.PasswordCredential({
              id: this.form.email,
              password: this.form.password,
              name: this.form.email,
            })
            await navigator.credentials.store(cred)
          } catch (credErr) {
            // Some browsers reject silently in non-secure contexts or
            // when the user has declined. Not fatal — login already
            // succeeded.
            console.debug('credentials.store skipped:', credErr)
          }
        }
        
        // If we reach here, login was successful
        this.showNotification({
          type: 'success',
          message: 'Successfully logged in!'
        })
        
        this.$emit('close')
        
        // Check for redirect query parameter or go to admin page
        const redirectTo = this.$route.query.redirect || '/admin'
        
        // Make sure the route exists before redirecting
        try {
          await this.$router.push(redirectTo)
        } catch (routeError) {
          // If the route doesn't exist, stay on current page
          console.log('Route does not exist, staying on current page')
        }
        
      } catch (error) {
        console.error('Login error:', error)
        
        // Extract error message properly
        let errorMessage = 'Invalid username or password'
        
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error.message) {
          errorMessage = error.message
        }
        
        // Display error message in the modal
        this.errorMessage = errorMessage
        
        // Don't show duplicate notification - error is already displayed in modal
        // this.showNotification is already called in the store action
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.auth-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.auth-modal-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 450px;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideIn 0.3s ease;
}

.auth-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.auth-modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.auth-modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.auth-modal-close:hover {
  color: #1f2937;
}

.auth-form {
  padding: 24px;
}

.error-message {
  background-color: #fee;
  color: #dc2626;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
}

.success-banner {
  background-color: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 500;
}

.invitation-banner {
  background-color: #eff6ff;
  color: #1e3a8a;
  border: 1px solid #bfdbfe;
  border-left: 4px solid #1a7fd4;
  padding: 12px 14px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.875rem;
  line-height: 1.5;
}

.invitation-banner i {
  color: #1a7fd4;
  font-size: 1.05rem;
  margin-top: 2px;
}

.invitation-banner strong {
  font-weight: 700;
  color: #0a2540;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  font-size: 0.875rem;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #1a7fd4;
  box-shadow: 0 0 0 3px rgba(26, 127, 212, 0.1);
}

.form-input:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.remember-me {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #6b7280;
}

.remember-me input[type="checkbox"] {
  cursor: pointer;
}

.forgot-password-link {
  color: #1a7fd4;
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.2s;
}

.forgot-password-link:hover {
  color: #0d6ebd;
  text-decoration: underline;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-primary {
  background-color: #1a7fd4;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0d6ebd;
}

.btn-primary:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.btn-block {
  width: 100%;
}

.auth-footer {
  margin-top: 24px;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
}

.auth-link {
  color: #1a7fd4;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s;
}

.auth-link:hover {
  color: #0d6ebd;
  text-decoration: underline;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@media (max-width: 640px) {
  .auth-modal-container {
    width: 95%;
    margin: 16px;
  }
  
  .auth-modal-header,
  .auth-form {
    padding: 20px;
  }
}
</style>